/**
 * @file SenderAddressSearch.tsx
 * @description Адрес склада отправления с подсказками DaData (phase19.12).
 *
 * Менеджер вводит обычный адрес — код населённого пункта перевозчика
 * подставляется сам. Ручной ввод кода спрятан в expander: он нужен редко
 * (особые случаи вроде пунктов без адреса в справочнике).
 */

import { ChevronDown, Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";

import { SenderWarehousePicker } from "./SenderWarehousePicker";
import type { SenderAddressResolver } from "./useSenderAddressResolver";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { addresses } from "../../lib/api-customers";
import { senderSummary, type SenderFormValue } from "../../lib/carrier-sender";
import type { DadataAddrSuggestion } from "../../lib/dadata-address";

interface Props {
  value: SenderFormValue;
  onChange: (patch: Partial<SenderFormValue>) => void;
  /** Разбор адреса живёт в родителе: город нужен и блоку пункта сдачи. */
  resolver: SenderAddressResolver;
  disabled?: boolean;
}

export function SenderAddressSearch({ value, onChange, resolver, disabled }: Props) {
  const [query, setQuery] = useState(value.senderAddress);
  const [suggestions, setSuggestions] = useState<DadataAddrSuggestion[]>([]);
  const [showList, setShowList] = useState(false);
  const [searching, setSearching] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  useEffect(() => {
    if (query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      void addresses
        .suggest(query.trim(), 8)
        .then((res) => setSuggestions(res.suggestions as DadataAddrSuggestion[]))
        .catch(() => setSuggestions([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const summary = senderSummary(value, resolver.cityName);

  return (
    <section className="flex flex-col gap-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
        Адрес склада (откуда забирает курьер / город расчёта)
      </div>

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 size-4 text-on-surface-variant" />
        <Input
          id="sender-address-search"
          className="pl-8"
          placeholder="Начните вводить адрес: Челябинск, ул. Кирова, 12"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange({ senderAddress: e.target.value });
            setShowList(true);
          }}
          onFocus={() => setShowList(true)}
          onBlur={() => setTimeout(() => setShowList(false), 200)}
        />
        {showList && suggestions.length > 0 ? (
          <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-lg bg-surface-lowest shadow-lg [box-shadow:0_8px_24px_rgba(42,47,26,0.12),inset_0_0_0_1px_var(--outline-variant)]">
            {suggestions.map((s) => (
              <li key={`${s.value}-${s.data.postal_code ?? ""}`}>
                <button
                  type="button"
                  className="flex min-h-11 w-full flex-col items-start px-3 py-2 text-left text-sm transition-colors hover:bg-surface-low"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQuery(s.value);
                    setShowList(false);
                    void resolver.applySuggestion(s);
                  }}
                >
                  <span>{s.value}</span>
                  {s.data.postal_code ? (
                    <span className="text-xs text-on-surface-variant">
                      Индекс {s.data.postal_code}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <SenderWarehousePicker
        disabled={disabled}
        onPick={(address) => {
          setQuery(address);
          void resolver.applyRawAddress(address);
        }}
      />

      {searching || resolver.resolving ? (
        <div className="flex items-center gap-1.5 text-xs text-on-surface-variant">
          <Loader2 className="size-3 animate-spin" />
          {resolver.resolving ? "Определяем город и код перевозчика…" : "Ищем адрес…"}
        </div>
      ) : null}

      {summary ? <p className="text-sm">{summary}</p> : null}
      {resolver.hint ? <p className="text-xs text-warning">{resolver.hint}</p> : null}

      <div className="flex flex-col gap-2">
        <button
          type="button"
          className="flex min-h-11 items-center gap-1 self-start text-sm text-on-surface-variant hover:text-on-surface"
          onClick={() => setManualOpen((v) => !v)}
          aria-expanded={manualOpen}
        >
          <ChevronDown className={`size-4 transition-transform ${manualOpen ? "rotate-180" : ""}`} />
          Ввести код города вручную (для особых случаев)
        </button>
        {manualOpen ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="senderCityCode">Код города СДЭК</Label>
              <Input
                id="senderCityCode"
                inputMode="numeric"
                placeholder="259"
                value={value.senderCityCode}
                disabled={disabled}
                onChange={(e) => onChange({ senderCityCode: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="senderPostalCode">Почтовый индекс</Label>
              <Input
                id="senderPostalCode"
                inputMode="numeric"
                placeholder="454000"
                value={value.senderPostalCode}
                disabled={disabled}
                onChange={(e) => onChange({ senderPostalCode: e.target.value })}
              />
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
